// InlineNodeChip — renders a single `[label]` inline reference from
// assistant text as a clickable chip. Hover bridges into the atlas
// spotlight (same focus state the verification-card breakdown uses).
//
// Matching strategy: the agent is instructed to wrap node display
// labels in square brackets like `[honest]` or `[warm without being
// soft]`. We try to resolve each bracketed label against the live
// snapshot's display_labels (exact match, case-insensitive). When a
// match is found, render the chip with the matched node's category
// color + node_id so hover can spotlight precisely. When no match,
// render the literal `[label]` text in muted style so it's still
// readable but obviously unresolved.

import type { GraphNode } from "@/lib/types";
import { cn } from "@/lib/utils";

const SECTOR_HUE: Record<string, number> = {
  ToneOfVoice: 28,
  Value: 260,
  RejectedValue: 260,
  Audience: 200,
  PhotographyStyle: 140,
  ColorPalette: 0,
  Color: 0,
  Note: 50,
  Ritual: 310,
};

interface Props {
  label: string;
  /** Resolved node from the snapshot, if a match exists. When null,
   *  the chip renders as an "unresolved" placeholder. */
  node: GraphNode | null;
  /** Push the node id into the atlas spotlight focus on hover.
   *  Empty array on leave restores the underlying state. */
  onHover?: (nodeIds: string[]) => void;
}

export function InlineNodeChip({ label, node, onHover }: Props) {
  const hue = node ? SECTOR_HUE[node.category] ?? 280 : 280;
  const resolved = !!node;

  return (
    <span
      onMouseEnter={() => {
        if (node && onHover) onHover([node.id]);
      }}
      onMouseLeave={() => {
        if (node && onHover) onHover([]);
      }}
      title={node ? `${node.category} · ${node.display_label || node.content}` : `Unresolved: ${label}`}
      className={cn(
        "inline-flex items-center gap-1 mx-0.5 px-1.5 py-px rounded-full text-[12px] align-baseline",
        "border whitespace-nowrap transition-colors duration-150 select-none",
        resolved ? "cursor-pointer" : "cursor-help"
      )}
      style={
        resolved
          ? {
              background: `oklch(0.97 0.04 ${hue})`,
              color: `oklch(0.35 0.14 ${hue})`,
              borderColor: `oklch(0.62 0.14 ${hue} / 0.3)`,
            }
          : {
              background: "transparent",
              color: "#9a958d",
              borderColor: "#e6e4df",
              borderStyle: "dashed",
            }
      }
    >
      <span
        className="h-1.5 w-1.5 rounded-full shrink-0"
        style={{ background: `oklch(0.62 0.14 ${hue})` }}
        aria-hidden
      />
      <span>{label}</span>
    </span>
  );
}

export type ChatToken =
  | { kind: "text"; text: string }
  | { kind: "bold"; text: string }
  | { kind: "chip"; label: string; node: GraphNode | null };

/** Split a piece of assistant text into (text | bold | chip) tokens by
 *  scanning for `**bold**` markers and `[label]` chip references. The
 *  agent emits light Markdown for visual structure (bold labels in
 *  paragraphs and lists); we render bold spans with `<strong>` so the
 *  hierarchy stays readable without pulling in a full Markdown stack.
 *
 *  We avoid greedy matching across newlines and respect markdown-style
 *  link syntax `[text](url)` — bracketed text immediately followed by
 *  `(` is left alone. Bold spans are matched non-greedily so `**a** **b**`
 *  parses as two spans, not one. */
export function parseTextWithChips(
  text: string,
  resolveLabel: (label: string) => GraphNode | null
): ChatToken[] {
  const out: ChatToken[] = [];
  // Combined regex: bold (group 1) OR chip (group 2). The first
  // alternative wins on overlap so `**[x]**` becomes bold then re-scans
  // its content — but the inner chip is rare in practice; bold labels
  // and chip references coexist as siblings in our prompts.
  const re = /\*\*([^*\n]{1,80})\*\*|\[([^\[\]\n]{1,80})\](?!\()/g;
  let lastIdx = 0;
  for (const m of text.matchAll(re)) {
    const start = m.index ?? 0;
    const end = start + m[0].length;
    if (start > lastIdx) {
      out.push({ kind: "text", text: text.slice(lastIdx, start) });
    }
    if (m[1] !== undefined) {
      out.push({ kind: "bold", text: m[1] });
    } else if (m[2] !== undefined) {
      const label = m[2].trim();
      const node = resolveLabel(label);
      out.push({ kind: "chip", label, node });
    }
    lastIdx = end;
  }
  if (lastIdx < text.length) {
    out.push({ kind: "text", text: text.slice(lastIdx) });
  }
  return out;
}
